import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  ArrowTopRightOnSquareIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  PhotoIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import classNames from "../../../utils/classNames";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import { buildMockPartIntelligence } from "./intelligenceMockData";
import {
  IPart,
  IPartIntelligence,
  IPartIntelligenceRecommendation,
} from "../../../models/Part";

const impactTone = (impact?: string | null) => {
  switch ((impact ?? "").toLowerCase()) {
    case "high":
      return "bg-red-50 text-red-700 ring-red-200";
    case "medium":
      return "bg-amber-50 text-amber-700 ring-amber-200";
    case "low":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";
    default:
      return "bg-gray-100 text-gray-700 ring-gray-200";
  }
};

const scoreTone = (n: number | null | undefined) => {
  if (typeof n !== "number") return "text-gray-400";
  if (n >= 80) return "text-emerald-600";
  if (n >= 60) return "text-sky-600";
  if (n >= 40) return "text-amber-600";
  return "text-red-600";
};

const scoreBar = (n: number | null | undefined) => {
  if (typeof n !== "number") return "bg-gray-300";
  if (n >= 80) return "bg-emerald-500";
  if (n >= 60) return "bg-sky-500";
  if (n >= 40) return "bg-amber-500";
  return "bg-red-500";
};

export default function StockIntelligence({ part }: { part: IPart }) {
  // Real data lives at `part.intelligence` once the backend ships it.
  // Until then we fall back to deterministic mock data so the tab can
  // be reviewed end-to-end. The `isDemo` flag drives a clear badge in
  // the hero so it's never mistaken for production data.
  const realIntel = part.intelligence ?? null;
  const isDemo = !realIntel;
  const intel = useMemo<IPartIntelligence | null>(
    () => realIntel ?? buildMockPartIntelligence(part),
    [realIntel, part]
  );

  const hasAny =
    !!intel &&
    (!!intel.pricing ||
      !!intel.listingQuality ||
      !!intel.demand ||
      (intel.recommendations?.length ?? 0) > 0 ||
      (intel.similarListings?.length ?? 0) > 0);

  return (
    <div className="flex flex-col gap-4">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-lg border border-violet-200 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-sky-50 p-4">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-fuchsia-200/40 to-sky-200/40 blur-2xl"
        />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 text-white shadow-sm">
              <SparklesIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Wilma Intelligence
                </h2>
                {isDemo && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-inset ring-amber-200">
                    Demo data
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600">
                AI-generated insights to help you price, list and sell this
                part faster.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {intel?.model && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 ring-1 ring-inset ring-violet-200">
                <BoltIcon className="h-3 w-3" aria-hidden="true" />
                {intel.model}
              </span>
            )}
            {intel?.generatedAt && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-3 w-3" aria-hidden="true" />
                Updated{" "}
                {formatDateTime(
                  intel.generatedAt,
                  DateTime.DATETIME_SHORT,
                  "-"
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {!hasAny && (
        <Card bodyClassName="p-8 text-center">
          <SparklesIcon
            className="mx-auto h-8 w-8 text-violet-300"
            aria-hidden="true"
          />
          <p className="mt-2 text-sm font-medium text-gray-700">
            Intelligence isn't available for this part yet
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Once the backend exposes the intelligence bundle (see{" "}
            <span className="font-mono">docs/pending-endpoints.md</span>),
            pricing suggestions, listing quality, demand forecasts and
            recommended actions will appear here.
          </p>
        </Card>
      )}

      {/* Pricing + Demand row */}
      {(intel?.pricing || intel?.demand) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {intel?.pricing && (
            <PricingInsight
              currentPrice={part.price ?? null}
              pricing={intel.pricing}
            />
          )}
          {intel?.demand && <DemandInsight demand={intel.demand} />}
        </div>
      )}

      {/* Listing Quality */}
      {intel?.listingQuality && (
        <ListingQualityCard quality={intel.listingQuality} />
      )}

      {/* Recommendations */}
      {(intel?.recommendations?.length ?? 0) > 0 && (
        <Card
          title="Recommended Actions"
          bodyClassName="p-4"
          headerRight={
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
              <SparklesIcon className="h-3 w-3" aria-hidden="true" />
              {intel!.recommendations!.length} suggestion
              {intel!.recommendations!.length === 1 ? "" : "s"}
            </span>
          }
        >
          <ul className="flex flex-col gap-2">
            {intel!.recommendations!.map((r) => (
              <RecommendationRow key={r.id} rec={r} />
            ))}
          </ul>
        </Card>
      )}

      {/* Similar Listings */}
      {(intel?.similarListings?.length ?? 0) > 0 && (
        <Card title="Similar Listings" bodyClassName="p-4">
          <p className="text-xs text-gray-500 mb-3">
            Live and recently-sold competitor listings used to benchmark this
            part.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {intel!.similarListings!.map((l) => (
              <a
                key={l.id}
                href={l.url ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex gap-3 rounded-md border border-gray-200 bg-white p-2 transition-colors hover:border-violet-300 hover:bg-violet-50/40"
              >
                <div className="h-16 w-16 flex-none overflow-hidden rounded bg-gray-100">
                  {l.thumbnailUrl ? (
                    <img
                      src={l.thumbnailUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <PhotoIcon
                      className="m-auto mt-4 h-8 w-8 text-gray-300"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-medium text-gray-900 group-hover:text-violet-700">
                    {l.title}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    {l.price != null && (
                      <span className="font-semibold text-gray-900">
                        {PriceFormat(l.price)}
                      </span>
                    )}
                    {l.condition && <span>· {l.condition}</span>}
                    {l.soldDate ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                        Sold
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">
                        Live
                      </span>
                    )}
                  </div>
                  {l.source && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-gray-400">
                      {l.source}
                      <ArrowTopRightOnSquareIcon
                        className="h-3 w-3"
                        aria-hidden="true"
                      />
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function PricingInsight({
  currentPrice,
  pricing,
}: {
  currentPrice: number | null;
  pricing: NonNullable<IPartIntelligence["pricing"]>;
}) {
  const suggested = pricing.suggestedPrice;
  const delta =
    typeof currentPrice === "number" && typeof suggested === "number"
      ? suggested - currentPrice
      : null;
  const deltaPct =
    delta != null && currentPrice && currentPrice !== 0
      ? (delta / currentPrice) * 100
      : null;

  const direction =
    delta == null ? null : delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <Card
      title="Suggested Price"
      bodyClassName="p-4"
      headerRight={
        typeof pricing.confidence === "number" ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
            <CheckBadgeIcon className="h-3 w-3" aria-hidden="true" />
            {Math.round(pricing.confidence)}% confident
          </span>
        ) : undefined
      }
    >
      <div className="flex items-end gap-3">
        <div>
          <p className="text-3xl font-semibold text-gray-900">
            {suggested != null ? PriceFormat(suggested) : "—"}
          </p>
          {direction && (
            <p
              className={classNames(
                "text-xs font-medium",
                direction === "up"
                  ? "text-emerald-600"
                  : direction === "down"
                    ? "text-red-600"
                    : "text-gray-500"
              )}
            >
              {direction === "up" ? "▲" : direction === "down" ? "▼" : "→"}{" "}
              {delta != null && PriceFormat(Math.abs(delta))}
              {deltaPct != null && ` (${Math.abs(deltaPct).toFixed(1)}%)`} vs
              current
            </p>
          )}
        </div>
      </div>
      {(pricing.lowerBand != null || pricing.upperBand != null) && (
        <p className="mt-2 text-xs text-gray-500">
          Recommended band:{" "}
          <span className="font-medium text-gray-700">
            {pricing.lowerBand != null ? PriceFormat(pricing.lowerBand) : "—"} –{" "}
            {pricing.upperBand != null ? PriceFormat(pricing.upperBand) : "—"}
          </span>
        </p>
      )}
      {pricing.reasoning && (
        <p className="mt-3 rounded-md bg-violet-50/60 px-3 py-2 text-xs leading-relaxed text-violet-900 ring-1 ring-inset ring-violet-100">
          <SparklesIcon
            className="mr-1 inline h-3.5 w-3.5 -translate-y-px"
            aria-hidden="true"
          />
          {pricing.reasoning}
        </p>
      )}
    </Card>
  );
}

function DemandInsight({
  demand,
}: {
  demand: NonNullable<IPartIntelligence["demand"]>;
}) {
  return (
    <Card title="Demand Forecast" bodyClassName="p-4">
      <div className="grid grid-cols-3 gap-3 mb-3">
        <ProbTile label="30d" value={demand.sellProbability30d} />
        <ProbTile label="60d" value={demand.sellProbability60d} />
        <ProbTile label="90d" value={demand.sellProbability90d} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Est. Days to Sell
          </div>
          <div className="mt-0.5 text-lg font-semibold text-gray-900">
            {demand.estimatedDaysToSell != null
              ? `${demand.estimatedDaysToSell} days`
              : "—"}
          </div>
        </div>
        <div className="rounded-md border border-gray-200 bg-gray-50/60 px-3 py-2">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            <ArrowTrendingUpIcon className="h-3 w-3" aria-hidden="true" />
            Trend
          </div>
          <div className="mt-0.5 text-lg font-semibold text-gray-900">
            {demand.trend ?? "—"}
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProbTile({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  const hasValue = typeof value === "number";
  const v = hasValue ? Math.max(0, Math.min(100, value!)) : 0;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2 py-2 text-center">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div
        className={classNames("mt-0.5 text-xl font-semibold", scoreTone(value))}
      >
        {hasValue ? `${v.toFixed(0)}%` : "—"}
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={classNames("h-full", scoreBar(value))}
          style={{ width: `${v}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

function ListingQualityCard({
  quality,
}: {
  quality: NonNullable<IPartIntelligence["listingQuality"]>;
}) {
  return (
    <Card title="Listing Quality" bodyClassName="p-4">
      <div className="flex items-center gap-4 mb-3">
        <div
          className={classNames(
            "flex h-16 w-16 flex-none items-center justify-center rounded-full ring-4",
            quality.overall >= 80
              ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
              : quality.overall >= 60
                ? "bg-sky-50 text-sky-700 ring-sky-100"
                : quality.overall >= 40
                  ? "bg-amber-50 text-amber-700 ring-amber-100"
                  : "bg-red-50 text-red-700 ring-red-100"
          )}
        >
          <span className="text-xl font-semibold">{Math.round(quality.overall)}</span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {quality.overall >= 80
              ? "Excellent"
              : quality.overall >= 60
                ? "Good"
                : quality.overall >= 40
                  ? "Needs work"
                  : "Poor"}
          </p>
          <p className="text-xs text-gray-500">
            Composite score across title, photos, description and fitment.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ScoreBar label="Title" value={quality.title} />
        <ScoreBar label="Photos" value={quality.photos} />
        <ScoreBar label="Description" value={quality.description} />
        <ScoreBar label="Fitment" value={quality.fitment} />
      </div>

      {(quality.issues?.length ?? 0) > 0 && (
        <div className="mt-4 rounded-md border border-amber-100 bg-amber-50/60 p-3">
          <p className="mb-1.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-800">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Issues to address
          </p>
          <ul className="ml-5 list-disc space-y-0.5 text-xs text-amber-900">
            {quality.issues!.map((issue, i) => (
              <li key={i}>{issue}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ScoreBar({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) {
  const hasValue = typeof value === "number";
  const v = hasValue ? Math.max(0, Math.min(100, value!)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <span>{label}</span>
        <span className={scoreTone(value)}>
          {hasValue ? Math.round(v) : "—"}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={classNames("h-full", scoreBar(value))}
          style={{ width: `${v}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

function RecommendationRow({ rec }: { rec: IPartIntelligenceRecommendation }) {
  return (
    <li className="flex gap-3 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-100">
        <LightBulbIcon className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-gray-900">{rec.title}</p>
          {rec.impact && (
            <span
              className={classNames(
                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset",
                impactTone(rec.impact)
              )}
            >
              {rec.impact}
            </span>
          )}
          {rec.category && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-200">
              {rec.category}
            </span>
          )}
        </div>
        {rec.description && (
          <p className="mt-1 text-xs leading-relaxed text-gray-600">
            {rec.description}
          </p>
        )}
      </div>
    </li>
  );
}
